import { Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { ServicesGrid } from "../components/sections/ServicesGrid";
import { Testimonials, reviewJsonLd } from "../components/sections/Testimonials";
import { InsightsPreview } from "../components/sections/InsightsPreview";
import { HealthCheck } from "../components/sections/HealthCheck";
import { VendorMarquee, VendorStack } from "../components/sections/VendorStack";
import { CTABanner } from "../components/sections/CTABanner";
import { Link } from "react-router-dom";

const EXPECTATIONS = [
  { k: "2015", v: "Founded — over a decade focused only on small business" },
  { k: "≤25", v: "Employees per client — small by choice, not by limitation" },
  { k: "15 min", v: "Response target for critical issues, 24/7/365" },
  { k: "24/7", v: "Human-led SOC watching your environment through Huntress" },
];

const PROMISES = [
  "Unlimited support hours — message us as often as you need, never an extra charge",
  "A standing CEO day — every client, every month, personally",
  "Every kind of IT problem covered, end to end",
];

export default function Home() {
  return (
    <>
      <Seo
        title="Sempresera | Managed IT Services for Small Business — National Service Provider"
        description="Sempresera is a national service provider for small businesses across the United States: 24/7 monitoring, Huntress-powered security, Microsoft 365 and backup — one all-inclusive standard."
        path="/"
        jsonLd={[reviewJsonLd]}
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pt-44" data-testid="hero-section">
        <MaskedLine delay={0.1}>
          <span className="eyebrow">National IT services · Est. 2015</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl lg:text-7xl">
          <MaskedLine delay={0.25}>
            <span className="text-metallic">Enterprise-grade IT,</span>
          </MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-metallic">built for small business.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.55}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#E2EAF4] md:text-lg">
            Sempresera is a national service provider for small businesses — managed IT with one all-inclusive
            standard, unlimited support hours, a full senior team, and security that never sleeps.
          </p>
        </MaskedLine>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/contact?mode=book" className="btn-primary" data-testid="hero-book-cta">Book a free consultation</Link>
          <Link to="/pricing" className="btn-ghost" data-testid="hero-pricing-cta">Explore the Standard</Link>
        </div>
      </section>

      <VendorMarquee />
      <ServicesGrid />

      <section className="border-t border-white/5" data-testid="why-us-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 py-24 md:grid-cols-12 md:px-8 md:py-32">
          <div className="md:col-span-5">
            <Reveal>
              <p className="eyebrow">Why Sempresera</p>
              <h2 className="mt-6 max-w-4xl font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl lg:text-7xl">
                <span className="text-metallic">Small by</span> <span className="text-metallic">choice.</span>
              </h2>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#C9D2DE] md:text-lg">
                Since 2015 we've served only small businesses — solo professionals, micro teams and firms of up to
                25 employees. Not because we can't go bigger, but because small businesses deserve the IT resources
                large corporations take for granted, delivered by people who actually know their names.
              </p>
              <ul className="mt-8 space-y-4">
                {PROMISES.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-zinc-300">
                    <Check size={18} className="mt-0.5 shrink-0 text-brand" strokeWidth={2.5} />
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {EXPECTATIONS.map((e, i) => (
                <Reveal key={e.k} delay={i * 0.08}>
                  <div className="glass h-full p-8" data-testid={`stat-${i}`}>
                    <p className="font-display text-5xl font-black tracking-tight text-metallic">{e.k}</p>
                    <p className="mt-4 text-sm leading-relaxed text-[#C9D2DE]">{e.v}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <HealthCheck />
      <VendorStack />
      <Testimonials />
      <InsightsPreview />
      <CTABanner />
    </>
  );
}
